import { STAGE_LABEL, type Stage } from '@/domain';
import { strings } from '@/shared/strings';
import { RowsIcon } from '@/ui/icons';
import styles from './ProductionLine.module.css';

export interface StageLoadView {
  stage: Stage;
  kits: number;
  overdue: number;
}

/**
 * The eighth card's counts, structurally what the board's `logistics` block
 * carries. Declared here rather than imported, as `StageLoadView` is: this
 * layer draws what it is given and does not reach for the port that fetched it.
 */
export interface LogisticsView {
  readyToDispatch: number;
  awaitingTracking: number;
  inTransit: number;
  held: number;
  overdue: number;
}

export interface ProductionLineProps {
  stages: StageLoadView[];
  activeKits: number;
  onOpenStage: (stage: Stage) => void;
  /**
   * The eighth card, after Ready: orders that have left Ready and not yet
   * arrived. Optional so a caller with an older board still draws seven.
   */
  logistics?: LogisticsView;
  onOpenLogistics?: () => void;
  /**
   * Drawn top to bottom, one stage under the other, instead of left to right.
   * The request of 5 October 2026 (third), section 9. The screen remembers the
   * choice and hands it in: this layer draws what it is given.
   */
  vertical?: boolean;
  /**
   * Called with the other layout when "Vertical view" is pressed. The button is
   * drawn only for a caller that passes this, as the eighth card is drawn only
   * for a caller that passes its counts.
   */
  onVerticalChange?: (vertical: boolean) => void;
}

/**
 * The production line, section 5.2.
 *
 * All seven stages in one straight line with connectors, and item 2 of section
 * 17 is explicit about why: this is not a branching flow. A flagged case can be
 * sent to any stage, so a fork would draw a route the system does not actually
 * have. The client asked for one line.
 *
 * The line runs left to right unless somebody chooses otherwise, and then it
 * scrolls sideways on a phone with momentum: the one place in the system where
 * horizontal scrolling is the design rather than a defect.
 *
 * "Vertical view" turns the same line top to bottom, a row for each stage with
 * its name, its count and what is late, and nothing scrolls sideways. The
 * request of 5 October 2026 (third), section 9. It is one button with a pressed
 * state, shown at every width. Both layouts are the same buttons in the same
 * order with the same names, so a tap, a screen reader and a test meet the same
 * line either way; only the stylesheet differs. Changing over is a cut, not an
 * animation: it would be animating layout.
 */
export function ProductionLine({
  stages,
  activeKits,
  onOpenStage,
  logistics,
  onOpenLogistics,
  vertical = false,
  onVerticalChange,
}: ProductionLineProps) {
  const showLogistics = logistics !== undefined && onOpenLogistics !== undefined;

  return (
    <div
      className={vertical ? `${styles.line} ${styles.vertical}` : styles.line}
      data-layout={vertical ? 'vertical' : 'horizontal'}
    >
      <div className={styles.top}>
        <span className={styles.live}>
          <span className={styles.pulse} />
          {strings.line.liveFloor}
        </span>
        <span className={styles.caption}>
          {activeKits} {strings.line.onTheLine}
        </span>
        {onVerticalChange !== undefined && (
          <button
            type="button"
            className={styles.layout}
            aria-pressed={vertical}
            onClick={() => onVerticalChange(!vertical)}
          >
            <RowsIcon size={16} />
            {strings.line.verticalView}
          </button>
        )}
      </div>

      <div className={styles.flow}>
        {stages.map((load, index) => (
          <div key={load.stage} className={styles.column}>
            <StageCard load={load} onOpen={() => onOpenStage(load.stage)} />
            {(index < stages.length - 1 || showLogistics) && <Connector />}
          </div>
        ))}
        {/*
          Logistics, after Ready. NOT an eighth stage: these are ORDERS that
          have left Ready and not yet arrived, and Stage still has seven values.
          Section 2 of the request of 13 September 2026.
        */}
        {showLogistics && (
          <div className={styles.column}>
            <LogisticsCard counts={logistics} onOpen={onOpenLogistics} />
          </div>
        )}
      </div>
    </div>
  );
}

/*
 * No load bar, and no capacity.
 *
 * The bar drew a percentage of a number nobody on the floor sets or checks;
 * the line under it then named that number outright. Both are gone. What is
 * left is the count, and how many of them are late, which is the whole of what
 * somebody reads a card for.
 */
function StageCard({ load, onOpen }: { load: StageLoadView; onOpen: () => void }) {
  const tone = load.kits === 0 ? 'idle' : load.overdue > 0 ? 'late' : 'ok';

  return (
    <button
      className={`${styles.card} ${load.stage === 'ready' ? styles.ready : ''}`}
      onClick={onOpen}
      aria-label={`${STAGE_LABEL[load.stage]}: ${load.kits} ${strings.line.kits}`}
    >
      <span className={styles.head}>
        <span className={styles.name}>{STAGE_LABEL[load.stage]}</span>
        <span className={`${styles.dot} ${styles[tone]}`} />
      </span>

      <span className={styles.count}>
        {load.kits}
        <small>{load.stage === 'ready' ? strings.line.ready : strings.line.kits}</small>
      </span>

      <span className={styles.foot}>
        {load.stage === 'ready'
          ? // The client's own words for this column.
            strings.line.awaitingDispatch
          : load.overdue > 0
            ? `${load.overdue} ${strings.line.overdue}`
            : strings.line.onTime}
      </span>
    </button>
  );
}

/**
 * The eighth card.
 *
 * Its line reads "3 waiting for a courier, 2 awaiting a waybill" rather than a
 * percentage of a capacity, because logistics has no capacity: a case on a van
 * is not taking up a bench.
 */
function LogisticsCard({ counts, onOpen }: { counts: LogisticsView; onOpen: () => void }) {
  const total = counts.readyToDispatch + counts.awaitingTracking + counts.inTransit + counts.held;
  const tone = total === 0 ? 'idle' : counts.overdue > 0 ? 'late' : 'ok';

  // Only the parts that have something in them, so the line says what is true
  // rather than four zeroes.
  const parts = [
    counts.readyToDispatch > 0 && `${counts.readyToDispatch} ${strings.line.logisticsWaiting}`,
    counts.awaitingTracking > 0 &&
      `${counts.awaitingTracking} ${strings.line.logisticsAwaitingWaybill}`,
    counts.inTransit > 0 && `${counts.inTransit} ${strings.line.logisticsInTransit}`,
    counts.held > 0 && `${counts.held} ${strings.line.logisticsHeld}`,
  ].filter((part): part is string => typeof part === 'string');

  return (
    <button
      className={`${styles.card} ${styles.ready}`}
      onClick={onOpen}
      aria-label={`${strings.line.logistics}: ${total} ${strings.line.logisticsOrders}`}
    >
      <span className={styles.head}>
        <span className={styles.name}>{strings.line.logistics}</span>
        <span className={`${styles.dot} ${styles[tone]}`} />
      </span>

      <span className={styles.count}>
        {total}
        <small>{strings.line.logisticsOrders}</small>
      </span>

      <span className={styles.foot}>
        {parts.length === 0 ? strings.line.logisticsEmpty : parts.join(' · ')}
      </span>
    </button>
  );
}

/**
 * The rail between two stages. Decorative, so it is hidden from the reading
 * order: a screen reader announcing "graphic" six times between seven stages
 * is noise, and the connection is already implied by the order of the list.
 * In the vertical view it is the rail alone, standing between two rows.
 */
function Connector() {
  return (
    <span className={styles.connector} aria-hidden="true">
      <span className={styles.rail} />
      <span className={styles.beam} />
      <span className={styles.beam} />
    </span>
  );
}
